/**
 * Pure helpers for the "Add data" spreadsheet (Figma 642:51593).
 *
 * A data variable's value is a 2D MATRIX of cell strings — rows of columns,
 * the same shape charts use (`TData = TDataCell[][]`) and the same shape the
 * in-content table spreadsheet is edited in. The whole table is populated from
 * the matrix, so there are no per-cell `[Key]` placeholders to match (that's the
 * *list* variable's template-cloning mechanism, which this deliberately is not).
 *
 * Values written before this shape existed were `Array<Record<string, unknown>>`,
 * so `toMatrix` accepts both and normalizes — an existing version keeps
 * working, with its object keys becoming the matrix's first (header) row.
 * A CSV upload is parsed straight to a matrix (see matrix-csv.ts).
 */

export type DataMatrix = string[][];

/** Minimum grid size so the sheet always looks like a sheet, per the design. */
export const MIN_GRID_COLUMNS = 5;
export const MIN_GRID_ROWS = 7;
/** Empty trailing rows/columns kept available so the sheet can always grow. */
export const SPARE_GRID_COLUMNS = 1;
export const SPARE_GRID_ROWS = 3;

/** 0 → "A", 25 → "Z", 26 → "AA" (spreadsheet column addresses). */
export const columnLetter = (index: number): string => {
  let rest = index;
  let letter = "";
  do {
    letter = String.fromCharCode(65 + (rest % 26)) + letter;
    rest = Math.floor(rest / 26) - 1;
  } while (rest >= 0);
  return letter;
};

const cellToString = (value: unknown): string =>
  value == null ? "" : String(value);

/**
 * Normalize any persisted value into a matrix.
 *
 * - `string[][]` (current shape) passes through, cells coerced to strings.
 * - `Array<Record<string, unknown>>` (legacy / CSV records) becomes a header
 *   row of the union of keys, followed by one row per record.
 * - anything else becomes an empty matrix.
 */
export const toMatrix = (value: unknown): DataMatrix => {
  if (!Array.isArray(value) || value.length === 0) return [];

  if (value.every((row) => Array.isArray(row))) {
    return (value as unknown[][]).map((row) => row.map(cellToString));
  }

  const records = value.filter(
    (row): row is Record<string, unknown> =>
      !!row && typeof row === "object" && !Array.isArray(row),
  );
  if (!records.length) return [];

  const headers: string[] = [];
  for (const record of records) {
    for (const key of Object.keys(record)) {
      if (!headers.includes(key)) headers.push(key);
    }
  }
  return [
    headers,
    ...records.map((record) =>
      headers.map((header) => cellToString(record[header])),
    ),
  ];
};

/**
 * Columns to render: always one spare beyond the widest used column, so the
 * sheet can always be extended sideways (filling the last column reveals the
 * next one). Never fewer than the minimum. Horizontal space is tighter than
 * vertical, hence one spare column vs. three spare rows.
 */
export const gridColumnCount = (matrix: DataMatrix): number =>
  Math.max(
    matrix.reduce((widest, row) => Math.max(widest, row.length), 0) +
      SPARE_GRID_COLUMNS,
    MIN_GRID_COLUMNS,
  );

/**
 * Rows to render: the data plus spare blank rows, so the sheet can always be
 * extended downwards. Never fewer than the minimum.
 */
export const gridRowCount = (matrix: DataMatrix): number =>
  Math.max(matrix.length + SPARE_GRID_ROWS, MIN_GRID_ROWS);

export const cellAt = (
  matrix: DataMatrix,
  rowIndex: number,
  columnIndex: number,
): string => matrix[rowIndex]?.[columnIndex] ?? "";

/**
 * Write one cell, growing the matrix so typing into a blank row/column creates
 * it. Returns a new matrix (never mutates).
 */
export const setCell = (
  matrix: DataMatrix,
  rowIndex: number,
  columnIndex: number,
  value: string,
): DataMatrix => {
  const next = matrix.map((row) => row.slice());
  while (next.length <= rowIndex) next.push([]);
  const row = next[rowIndex];
  while (row.length <= columnIndex) row.push("");
  row[columnIndex] = value;
  return next;
};

const isBlankRow = (row: string[]): boolean =>
  row.every((cell) => cell.trim() === "");

/**
 * What actually gets committed: trailing blank rows and trailing blank columns
 * dropped, so an untouched 7x5 sheet with four filled cells commits four
 * single-column rows rather than 35 empty strings. Interior blanks are kept —
 * a gap inside the data is real (an empty cell in the middle of a table).
 */
export const trimMatrix = (matrix: DataMatrix): DataMatrix => {
  const rows = matrix.map((row) => row.map(cellToString));
  let lastRow = rows.length - 1;
  while (lastRow >= 0 && isBlankRow(rows[lastRow])) lastRow -= 1;
  const kept = rows.slice(0, lastRow + 1);
  if (!kept.length) return [];

  let width = 0;
  for (const row of kept) {
    for (let index = row.length - 1; index >= 0; index -= 1) {
      if (row[index].trim() !== "") {
        width = Math.max(width, index + 1);
        break;
      }
    }
  }
  if (width === 0) return [];
  return kept.map((row) => {
    const trimmed = row.slice(0, width);
    while (trimmed.length < width) trimmed.push("");
    return trimmed;
  });
};
